import { IAwsCredentials } from "../types/kinesis";
import { KinesisVideoService } from "./kinesisVideo.service";
import { KinesisVideoSignalingClientService } from "./kinesisVideoSignaling.service";
import { SignalingClient, Role } from "amazon-kinesis-video-streams-webrtc";
import { v4 as uuidv4 } from 'uuid';

export class WebRTCCallService {
  awsConfigs: IAwsCredentials;
  peerConnection: RTCPeerConnection | null;
  private channelData: any;
  signalingClient: SignalingClient | null;
  kinesisVideoService: KinesisVideoService;
  audioActive: boolean;
  localStream: MediaStream;


  constructor(credentials: IAwsCredentials) {
    this.kinesisVideoService = new KinesisVideoService(credentials);
    this.awsConfigs = credentials;
    this.peerConnection = null;
    this.signalingClient = null;
    this.localStream = new MediaStream();
    this.audioActive = true;
  }

  async startCall(
    audioRef: React.MutableRefObject<HTMLAudioElement | null>,
    handleAudioConnection: (connected: boolean) => void,
    channelARN: string,
    handleAudioCallStatus: (connected: boolean) => void,
    handlePublishEvent: (data: any, status: string, topicKey: string) => void,
  ) {
    try {
      // Get Signaling Channel Endpoints

      const channelResponse = { channelARN };
      this.channelData = channelResponse;
      handlePublishEvent(channelResponse, "start", "call");
      const getSignalingChannelEndpointResponse =
        await this.kinesisVideoService.getSignallingChannelEndpoint(
          this.channelData.channelARN,
        );

      let wssProtocol: string | undefined = "";
      const endpointsByProtocol =
        getSignalingChannelEndpointResponse.ResourceEndpointList?.reduce(
          (endpoints: any, endpoint: any) => {
            if (endpoints.Protocol === "WSS") {
              wssProtocol = endpoint.ResourceEndpoint;
            }
            endpoints[endpoint.Protocol!!] = endpoint.ResourceEndpoint;
            return endpoints;
          },
          {},
        );

      const kinesisVideoSignalingChannelsClient =
        new KinesisVideoSignalingClientService({
          endpoint: endpointsByProtocol ? endpointsByProtocol["HTTPS"] : "",
          ...this.awsConfigs,
        });

      // Get IceServerConfig
      const getIceServerConfigResponse =
        await kinesisVideoSignalingChannelsClient.getIceServerConfig(
          this.channelData.channelARN,
        );

      const iceServers: any = [];

      // Add ICE servers
      if (process.env.REACT_APP_NAT_TRAVERSAL == "TURN") {
        getIceServerConfigResponse.IceServerList?.forEach((iceServer) => {
          iceServers.push({
            urls: iceServer.Uris,
            username: iceServer.Username,
            credential: iceServer.Password,
          });
        });
      } else {
        iceServers.push({
          urls: `stun:stun.kinesisvideo.${this.awsConfigs.region}.amazonaws.com:443`,
        });
      }

      // Create WebRtc Signaling Client
      const signalingClient = new SignalingClient({
        ...this.awsConfigs,
        channelARN: this.channelData.channelARN,
        role: Role.VIEWER,
        channelEndpoint: endpointsByProtocol ? endpointsByProtocol["WSS"] : "",
        clientId: uuidv4(),
      });

      const constraints = {
        video: false,
        audio: true,
      };

      // Create RTCPeer Connection
      const peerConnection = new RTCPeerConnection({ iceServers });

      this.peerConnection = peerConnection;
      this.signalingClient = signalingClient;

      // Add Signaling Client Event Listeners
      signalingClient.on("open", async () => {
        console.log("[VIEWER] Connected to signaling service");

        try {
          this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

          this.localStream.getTracks().forEach((track) =>
            peerConnection.addTrack(track, this.localStream)
          );

          audioRef.current!.srcObject = this.localStream;

          console.log("[VIEWER] Microphone Access Provided");
        } catch (e) {
          this.endCall(handleAudioConnection, handlePublishEvent);
          // notification.error({
          //   message: "Error",
          //   description: "Microphone Access Blocked",
          // });
          // console.error(
          //   `[VIEWER] Could not find ${Object.keys(constraints).filter(
          //     (k) => constraints[k],
          //   )} input device.`,
          //   e,
          // );
          return;
        }

        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);

        // Check if localDescription exists
        if (peerConnection.localDescription) {
          // Send the offer to the signaling service
          signalingClient.sendSdpOffer(peerConnection.localDescription);
        } else {
          console.error("Local description is null");
        }
      });

      signalingClient.on("sdpAnswer", async (answer) => {
        console.log("[VIEWER] Received SDP answer", peerConnection);
        await peerConnection.setRemoteDescription(answer);
      });

      signalingClient.on("iceCandidate", (candidate) => {
        console.log("[VIEWER] Received ICE candidate");
        peerConnection.addIceCandidate(candidate);
      });

      // Add Peer Connection Event Listeners

      peerConnection.addEventListener("icecandidate", ({ candidate }) => {
        if (candidate) {
          handleAudioCallStatus(false);
          console.log("[VIEWER] Generated ICE candidate");
          signalingClient.sendIceCandidate(candidate);
        } else {
          console.log("[VIEWER] All ICE candidates have been generated");
        }
      });

      // Set up remote audio stream
      peerConnection.addEventListener("track", (event) => {
        if (event.track.kind === "audio") {
          setTimeout(() => {
            handleAudioCallStatus(true);
          }, 600);
          console.log("[VIEWER] Received remote audio track");
          if (event.streams.length > 0) {
            // Attach the remote audio track to the audio element for playback
            const remoteStream = event.streams[0];
            if (remoteStream) {
              remoteStream.getTracks().forEach((track) => {
                if (track.kind === "audio") {
                  audioRef.current!.srcObject = new MediaStream([track]);
                  audioRef.current!.play().catch((error) => {
                    console.error("Error playing remote audio:", error);
                  });
                }
              });
            }
          }
        }
      });

      peerConnection.addEventListener("iceconnectionstatechange", () => {
        if (peerConnection) {
          console.log(
            "[VIEWER] Ice Connection State:",
            peerConnection.iceConnectionState,
          );
          const connection =
            peerConnection.iceConnectionState === "connected" ? true : false;
          handleAudioConnection(connection);
          if (connection) {
            this.audioActive = true;
          }

          if (peerConnection.iceConnectionState === "disconnected") {
            console.log("[VIEWER] Peer connection disconnected");
            this.endCall(handleAudioConnection, handlePublishEvent);
          }
          if (peerConnection.iceConnectionState === "closed") {
            console.log("[VIEWER] Peer connection closed");
            // this.endCall(); // Call endCall method when connection is closed
          }
        }
      });

      signalingClient.on("error", (reason) => {
        // notification.error({
        //   message: "Error",
        //   description: "Error in connecting",
        // });
        console.log("[VIEWER]peerConnection Error in signaling client", reason);
      });

      signalingClient.open();
    } catch (e) {
      // notification.error({
      //   message: "Error",
      //   description: "Encountered error starting:",
      // });
      this.endCall(handleAudioConnection, handlePublishEvent);
      console.error("[VIEWER] Encountered error starting:", e);
    }
  }

  endCall(
    handleAudioConnection: (connected: boolean) => void,
    handlePublishEvent: (data: any, status: string, topicKey: string) => void,
  ) {
    console.log("topic ending.....");

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream =  new MediaStream();
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      handleAudioConnection(false);
      this.peerConnection = null;
      handlePublishEvent(this.channelData, "end", "call");
    }
    if (this.signalingClient) {
      this.signalingClient.close();
      this.signalingClient = null;
    }
    if (this.peerConnection == null || this.signalingClient == null) {
      handleAudioConnection(false);
    }
  }

  toggleAudioMute(handleMuted: (muted: boolean) => void) {
    if (this.peerConnection) {
      this.peerConnection.getSenders().forEach((sender) => {
        if (sender.track?.kind === "audio") {
          const trackEnabled = !this.audioActive;
          sender.track.enabled = trackEnabled;
          console.log(sender.track.enabled, "sender.track.enabled");
          this.audioActive = trackEnabled;
          console.log(this.audioActive, "this.audioActive");

          handleMuted(trackEnabled);
        }
      });
    }
  }
}
